import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import DownloadIcon from '@mui/icons-material/Download'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import UndoIcon from '@mui/icons-material/Undo'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid'

import {
    bannerSx,
    Breadcrumbs,
    buttonSx,
    cardSx,
    chipSx,
    classToFComponent,
    dataGridSx,
    EmptyIcon,
    FCClassContext,
    LifesHubChipTone,
    ModuleView,
    PageHeadGrid,
    PageSubtitle,
    PageTitle,
    SkeletonBlock
} from 'src/utils/views'
import {
    competenciaKey,
    formatCompetencia,
    formatCurrency,
    formatMonthLabel,
    formatNumber
} from 'src/utils/formatter-utils'

import BillingScope from '../bl_scopes'
import type { BillingLine, BillingStatus, BillingUserLine } from '../bl_types'
import BillingCard, { BillingCardHeader } from './bl_card'
import BillingChartMonthlyView from './bl_chart_monthly'
import BillingFilterPaneView from './bl_filter_pane'
import BillingInvoiceDialogView from './bl_invoice_dialog'
import { BillingOriginsChartView, BillingOriginsTableView } from './bl_origins'

const LOG = Logger.get('BL-VIEW')

const DashboardView = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gridTemplateRows: 'auto auto 300px minmax(420px, 1fr) 440px',
    gridTemplateAreas: `
        "head    head    head    head    head    head"
        "kpis    kpis    kpis    kpis    kpis    kpis"
        "chart   chart   chart   chart   chart   chart"
        "groups  groups  groups  groups  users   users"
        "origins origins origins origins originsChart originsChart"
    `,
    gap: 11,
    padding: '26px 28px 42px',
    overflowX: 'hidden',
    overflowY: 'auto',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    '@media (max-width: 1050px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateRows: 'auto auto 300px minmax(420px, auto) minmax(360px, auto) 440px 300px',
        gridTemplateAreas: `
            "head"
            "kpis"
            "chart"
            "groups"
            "users"
            "origins"
            "originsChart"
        `
    },
    '@media (max-width: 720px)': {
        padding: '22px 18px 36px',
        gap: 10
    }
})

const EmptyState = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '48px 20px',
    textAlign: 'center',
    '& h3': { margin: 0, fontSize: 16, color: 'var(--ink)' },
    '& p': { margin: 0, color: 'var(--muted)', maxWidth: 420 }
})

const SELECTED_ROW = 'bl-selected-row'
const INVOICED_ROW = 'bl-invoiced-row'
const NO_CHARGE_ROW = 'bl-no-charge-row'

const GRID_BASE = dataGridSx

const GRID_SELECTABLE = {
    ...GRID_BASE,
    '& .MuiDataGrid-row': {
        cursor: 'pointer'
    },
    '& .MuiDataGrid-row:hover': {
        background: 'linear-gradient(90deg, var(--green-050), #fbfffb)'
    },
    [`& .MuiDataGrid-row.${SELECTED_ROW}`]: {
        background: 'linear-gradient(90deg, var(--green-100), #fbfffb)',
        boxShadow: 'inset 4px 0 0 var(--green-700)',
        '&:hover': {
            background: 'linear-gradient(90deg, var(--green-100), var(--green-050))'
        }
    },
    [`& .MuiDataGrid-row.${INVOICED_ROW}`]: {
        backgroundColor: 'var(--blue-bg)',
        '&:hover': {
            backgroundColor: '#e3edfd'
        }
    },
    [`& .MuiDataGrid-row.${NO_CHARGE_ROW}`]: {
        backgroundColor: 'var(--green-050)',
        '&:hover': {
            backgroundColor: '#eafbea'
        }
    }
}

const useStyles = makeStyles()({
    overlay: {
        flexShrink: 0,
        marginRight: 5,
        marginBottom: 5
    },
    kpis: {
        gridArea: 'kpis',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 8,
        '@media (max-width: 720px)': {
            gridTemplateColumns: 'minmax(0, 1fr)'
        }
    },
    chart: { gridArea: 'chart', minWidth: 0, minHeight: 0 },
    groups: { gridArea: 'groups', minWidth: 0, minHeight: 0 },
    users: { gridArea: 'users', minWidth: 0, minHeight: 0 },
    origins: { gridArea: 'origins', minWidth: 0, minHeight: 0 },
    originsChart: { gridArea: 'originsChart', minWidth: 0, minHeight: 0 },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
        padding: '0 18px 18px'
    },
    banner: {
        margin: '0 18px 12px'
    },
    grid: {
        ...GRID_BASE
    },
    clickableGrid: {
        ...GRID_SELECTABLE
    }
})

type BillingViewProps = {
    className?: string
    scope: BillingScope
}

class BillingViewClass implements FCClassContext<BillingViewProps> {
    scope!: BillingScope
    classes!: ReturnType<typeof useStyles>['classes']

    readonly emitDownload = () => this.scope.onDownload().catch(LOG.caught)
    readonly emitInvoice = () => this.scope.onInvoiceSelected().catch(LOG.caught)
    readonly emitReverse = () => this.scope.onReverseSelected().catch(LOG.caught)

    readonly emitSelectLine = (params: GridRowParams) => {
        const line = params.row as BillingLine
        this.scope.onSelectLine(line.entityType, line.entityId, line.mesAno).catch(LOG.caught)
    }

    readonly rowClassName = (params: GridRowParams) => {
        const line = params.row as BillingLine
        const { selectedEntityType, selectedEntityId, selectedPeriod } = this.scope

        const selected =
            line.entityType === selectedEntityType &&
            line.entityId === selectedEntityId &&
            competenciaKey(line.mesAno) === selectedPeriod

        const noCharge = line.status === 'DENTRO_DA_FRANQUIA' || line.status === 'ILIMITADO'

        return [selected ? SELECTED_ROW : '', line.invoice ? INVOICED_ROW : '', noCharge ? NO_CHARGE_ROW : '']
            .filter(Boolean)
            .join(' ')
    }

    onSyncState({ scope }: BillingViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    render({ className, scope }: BillingViewProps) {
        this.scope = bindUpdate(React, scope)
        const { classes } = this

        return (
            <ModuleView className={className}>
                <DashboardView>
                    {this.renderPageHead()}
                    {scope.waiting ? this.renderSkeleton() : this.renderDashboard()}
                </DashboardView>
                <BillingFilterPaneView className={classes.overlay} scope={scope.filterPane} />
                {!!scope.invoiceDialog && <BillingInvoiceDialogView scope={scope.invoiceDialog} />}
            </ModuleView>
        )
    }

    renderPageHead() {
        return (
            <PageHeadGrid>
                <div>
                    <Breadcrumbs>
                        Administração / <b>Faturamento</b>
                    </Breadcrumbs>
                    <PageTitle>Faturamento</PageTitle>
                    <PageSubtitle>
                        Acompanhe o consumo de exportações por grupo, por usuário e por origem, apure o excedente de
                        cada competência e feche a cobrança.
                    </PageSubtitle>
                </div>
            </PageHeadGrid>
        )
    }

    renderSkeleton() {
        const { classes } = this

        return (
            <>
                <div className={classes.kpis}>
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                        <SkeletonBlock key={index} sx={{ height: 92 }} />
                    ))}
                </div>
                <SkeletonBlock className={classes.chart} />
                <SkeletonBlock className={classes.groups} />
                <SkeletonBlock className={classes.users} />
                <SkeletonBlock className={classes.origins} />
                <SkeletonBlock className={classes.originsChart} />
            </>
        )
    }

    renderDashboard() {
        const { classes, scope } = this

        return (
            <>
                <div className={classes.kpis}>
                    <BillingKpiTile label="Registros exportados" value={formatNumber(scope.totalExportado)} />
                    <BillingKpiTile label="Registros excedentes" value={formatNumber(scope.totalExcedente)} />
                    <BillingKpiTile
                        label="Total a faturar"
                        value={formatCurrency(scope.valorAFaturar)}
                        hintTone="warning"
                        hint={
                            scope.entidadesSemTabela > 0
                                ? `${scope.entidadesSemTabela} sem tabela de preço configurada`
                                : undefined
                        }
                    />
                    <BillingKpiTile
                        label="Total faturado"
                        value={formatCurrency(scope.valorFaturado)}
                        hint={
                            scope.linhasFaturadas > 0
                                ? `${pluralize(scope.linhasFaturadas, 'competência fechada', 'competências fechadas')}`
                                : 'Nenhuma competência fechada'
                        }
                    />
                    <BillingKpiTile
                        label="Total cortesia"
                        value={formatCurrency(scope.valorCortesia)}
                        hint={
                            scope.linhasComCortesia > 0
                                ? `${pluralize(
                                      scope.linhasComCortesia,
                                      'competência com desconto',
                                      'competências com desconto'
                                  )}`
                                : undefined
                        }
                    />
                    <BillingKpiTile label="Com excedente" value={formatNumber(scope.entidadesComExcedente)} />
                </div>

                <BillingChartMonthlyView className={classes.chart} scope={scope.monthly} />

                {this.renderGroups()}
                {this.renderUsers()}

                <BillingOriginsTableView className={classes.origins} scope={scope.origins} />
                <BillingOriginsChartView className={classes.originsChart} scope={scope.origins} />
            </>
        )
    }

    renderGroups() {
        const { classes, scope } = this

        return (
            <BillingCard elevation={0} className={classes.groups}>
                <BillingCardHeader
                    title="Cobrança por grupo / usuário"
                    count={pluralize(scope.lines.length, 'competência encontrada', 'competências encontradas')}
                    actions={this.renderInvoiceActions()}
                />
                {scope.entidadesSemTabela > 0 && (
                    <Alert
                        className={classes.banner}
                        variant="outlined"
                        icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                        sx={bannerSx('warning')}
                    >
                        Existem linhas sem tabela de preço vigente na competência — o valor não pôde ser calculado.
                    </Alert>
                )}
                {scope.lines.length === 0 ? (
                    <EmptyState>
                        <EmptyIcon>
                            <InfoOutlinedIcon />
                        </EmptyIcon>
                        <h3>Nenhuma competência encontrada</h3>
                        <p>
                            Ajuste o intervalo de competências, o grupo ou o recorte de excedente no painel de filtros.
                        </p>
                    </EmptyState>
                ) : (
                    <div className={classes.cardBody}>
                        <DataGrid
                            className={classes.clickableGrid}
                            rows={scope.lines}
                            columns={GROUP_COLUMNS}
                            getRowId={rowIdByLine}
                            onRowClick={this.emitSelectLine}
                            getRowClassName={this.rowClassName}
                            disableRowSelectionOnClick
                            density="compact"
                            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                            pageSizeOptions={[10, 25, 50, 100]}
                        />
                    </div>
                )}
            </BillingCard>
        )
    }

    renderInvoiceActions() {
        const { scope } = this
        const line = scope.selectedLine
        const faturado = !!line?.invoice

        const podeFaturar = line?.status === 'A_FATURAR'
        const invoiceHint = line ? INVOICE_HINT[line.status] : 'Selecione uma linha para faturar'

        return (
            <>
                <Tooltip title="Baixar a planilha das competências em tela">
                    <span>
                        <Button
                            sx={buttonSx('default', true)}
                            onClick={this.emitDownload}
                            disabled={scope.downloading || scope.lines.length === 0}
                            startIcon={scope.downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                        >
                            Baixar planilha
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={faturado ? 'Estornar o faturamento' : 'Nada a estornar na linha selecionada'}>
                    <span>
                        <Button
                            sx={buttonSx('danger', true)}
                            onClick={this.emitReverse}
                            disabled={!faturado}
                            startIcon={<UndoIcon />}
                        >
                            Estornar
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={invoiceHint}>
                    <span>
                        <Button
                            sx={buttonSx('primary', true)}
                            onClick={this.emitInvoice}
                            disabled={!podeFaturar}
                            startIcon={<ReceiptLongIcon />}
                        >
                            Faturar competência
                        </Button>
                    </span>
                </Tooltip>
            </>
        )
    }

    renderUsers() {
        const { classes, scope } = this

        const competencia = scope.selectedPeriod ? ` :: ${formatMonthLabel(scope.selectedPeriod)}` : ''

        const count = scope.selectedEntityName
            ? `${scope.selectedEntityName}${competencia} — ${pluralize(scope.detailRows.length, 'usuário', 'usuários')}`
            : undefined

        return (
            <BillingCard elevation={0} className={classes.users}>
                <BillingCardHeader
                    title="Detalhamento por usuário"
                    count={count}
                    actions={scope.detailLoading ? <CircularProgress size={16} /> : undefined}
                />
                {!scope.selectedEntityId ? (
                    <Alert
                        className={classes.banner}
                        variant="outlined"
                        icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                        sx={bannerSx('blue')}
                    >
                        Selecione uma linha na tabela de cobrança — grupo ou usuário, por competência — para ver o
                        detalhamento.
                    </Alert>
                ) : (
                    <div className={classes.cardBody}>
                        <DataGrid
                            className={classes.grid}
                            rows={scope.detailRows}
                            columns={USER_COLUMNS}
                            getRowId={rowIdByUserLine}
                            disableRowSelectionOnClick
                            density="compact"
                            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                            pageSizeOptions={[10, 25, 50, 100]}
                        />
                    </div>
                )}
            </BillingCard>
        )
    }
}

export const BillingView = classToFComponent(BillingViewClass, React)
export default BillingView

function pluralize(count: number, singular: string, plural: string): string {
    return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

const INVOICE_HINT: Record<BillingStatus, string> = {
    A_FATURAR: 'Faturar a competência selecionada',
    FATURADO: 'Competência já faturada',
    ISENTO: 'Competência já faturada (isenta)',
    DENTRO_DA_FRANQUIA: 'Consumo dentro da franquia — não há excedente a faturar',
    ILIMITADO: 'Franquia ilimitada — não há excedente a faturar',
    SEM_TABELA: 'Sem tabela de preço vigente — não há valor apurado',
    SEM_FAIXA: 'Sem faixa de preço aplicável — não há valor apurado'
}

const STATUS_LABEL: Record<BillingStatus, string> = {
    A_FATURAR: 'A faturar',
    FATURADO: 'Faturado',
    ISENTO: 'Isento',
    DENTRO_DA_FRANQUIA: 'Dentro da franquia',
    ILIMITADO: 'Ilimitada',
    SEM_TABELA: 'Sem tabela de preço',
    SEM_FAIXA: 'Sem faixa aplicável'
}

const STATUS_TONE: Record<BillingStatus, LifesHubChipTone> = {
    A_FATURAR: 'orange',
    FATURADO: 'blue',
    ISENTO: 'purple',
    DENTRO_DA_FRANQUIA: 'green',
    ILIMITADO: 'gray',
    SEM_TABELA: 'red',
    SEM_FAIXA: 'red'
}

function formatDateTime(value: string, dateOnly = false): string {
    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return '—'
    }

    return dateOnly ? date.toLocaleDateString('pt-BR') : date.toLocaleString('pt-BR')
}

function formatPercent(value: number): string {
    return `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
}

function formatOptionalCurrency(value: unknown): string {
    return value === null || value === undefined ? '—' : formatCurrency(Number(value))
}

const GROUP_COLUMNS: GridColDef[] = [
    {
        field: 'mesAno',
        headerName: 'Comp.',
        width: 82,
        valueFormatter: (value) => formatCompetencia(value as string)
    },
    {
        field: 'entityName',
        headerName: 'Grupo / Usuário',
        flex: 1,
        minWidth: 190,
        renderCell: ({ row }) => {
            const line = row as BillingLine
            return (
                <span
                    style={{ fontWeight: 750 }}
                    title={line.entityType === 'USER' ? 'Usuário com franquia própria' : 'Grupo'}
                >
                    {line.entityName}
                    {line.entityType === 'USER' ? ' (franquia própria)' : ''}
                </span>
            )
        }
    },
    {
        field: 'franquia',
        headerName: 'Franquia',
        width: 104,
        type: 'number',
        valueGetter: (_value, row) => (row as BillingLine).franquia,
        renderCell: ({ row }) => {
            const line = row as BillingLine
            return <span>{line.ilimitado ? 'Ilimitada' : formatNumber(line.franquia)}</span>
        }
    },
    {
        field: 'qtdExportada',
        headerName: 'Exportado',
        width: 104,
        type: 'number',
        valueFormatter: (value) => formatNumber(Number(value))
    },
    {
        field: 'qtdExcedente',
        headerName: 'Excedente',
        width: 104,
        type: 'number',
        valueFormatter: (value) => formatNumber(Number(value))
    },
    {
        field: 'tierMinQty',
        headerName: 'Faixa',
        width: 104,
        valueGetter: (_value, row) => {
            const line = row as BillingLine
            if (line.tierMinQty === null) {
                return '—'
            }
            return line.tierMaxQty === null ? `${line.tierMinQty}+` : `${line.tierMinQty} a ${line.tierMaxQty}`
        }
    },
    {
        field: 'unitPrice',
        headerName: 'Valor un.',
        width: 100,
        type: 'number',
        valueFormatter: (value) => formatOptionalCurrency(value)
    },
    {
        field: 'valor',
        headerName: 'Valor total',
        width: 120,
        type: 'number',
        valueFormatter: (value) => formatOptionalCurrency(value)
    },
    {
        field: 'priceTableSource',
        headerName: 'Tabela',
        width: 96,
        renderCell: ({ value }) => {
            if (value === 'GROUP') {
                return <Chip size="small" label="Própria" sx={chipSx('blue')} />
            }
            if (value === 'DEFAULT') {
                return <Chip size="small" label="Padrão" sx={chipSx('gray')} />
            }
            return <span>—</span>
        }
    },
    {
        field: 'invoice',
        headerName: 'Faturamento',
        width: 130,
        sortable: false,
        renderCell: ({ row }) => {
            const { invoice } = row as BillingLine

            if (!invoice) {
                return <span>—</span>
            }

            const detalhe = [
                `Faturado em ${formatDateTime(invoice.faturadoEm)}`,
                invoice.faturadoPorNome ? `por ${invoice.faturadoPorNome}` : '',
                invoice.descontoCentavos > 0
                    ? `Cortesia de ${formatCurrency(invoice.descontoCentavos / 100)} sobre ${formatCurrency(
                          invoice.valorCalculadoCentavos / 100
                      )}`
                    : '',
                invoice.observacao ?? ''
            ]
                .filter(Boolean)
                .join('\n')

            return (
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{detalhe}</span>}>
                    <span>{formatDateTime(invoice.faturadoEm, true)}</span>
                </Tooltip>
            )
        }
    },
    {
        field: 'status',
        headerName: 'Situação',
        width: 160,
        renderCell: ({ value }) => {
            const status = value as BillingStatus
            return <Chip size="small" label={STATUS_LABEL[status]} sx={chipSx(STATUS_TONE[status])} />
        }
    }
]

const USER_COLUMNS: GridColDef[] = [
    {
        field: 'mesAno',
        headerName: 'Comp.',
        width: 82,
        valueFormatter: (value) => formatCompetencia(value as string)
    },
    {
        field: 'userName',
        headerName: 'Usuário',
        flex: 1,
        minWidth: 160,
        renderCell: ({ value }) => <span style={{ fontWeight: 750 }}>{value as string}</span>
    },
    {
        field: 'qtdExportada',
        headerName: 'Exportado',
        width: 140,
        type: 'number',
        renderCell: ({ row }) => {
            const line = row as BillingUserLine
            return <span>{`${formatNumber(line.qtdExportada)} (${formatPercent(line.participacaoPct)})`}</span>
        }
    },
    {
        field: 'qtdExcedenteCronologico',
        headerName: 'Excedente',
        width: 104,
        type: 'number',
        valueFormatter: (value) => formatNumber(Number(value))
    },
    {
        field: 'valorRateado',
        headerName: 'Valor',
        width: 104,
        type: 'number',
        valueFormatter: (value) => formatOptionalCurrency(value)
    }
]

const rowIdByLine = (row: unknown) => {
    const line = row as BillingLine
    return `${line.entityType}-${line.entityId}-${line.mesAno}`
}

const rowIdByUserLine = (row: unknown) => {
    const line = row as BillingUserLine
    return `${line.userId}-${line.mesAno}`
}

const TileContainer = styled(Paper)({
    ...cardSx,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '16px 18px',
    minWidth: 0,
    flexGrow: 1
})

const TileValue = styled('span')({
    fontSize: 26,
    lineHeight: 1,
    fontWeight: 800,
    color: 'var(--ink)',
    marginBottom: 7,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
})

const TileLabel = styled('span')({
    fontSize: 12,
    color: 'var(--muted)'
})

const TileHint = styled('span')({
    marginTop: 5,
    fontSize: 11.5,
    lineHeight: 1.3
})

type BillingKpiTileProps = {
    label: string
    value: string
    hint?: string
    hintTone?: 'muted' | 'warning'
    className?: string
}

function BillingKpiTile({ label, value, hint, hintTone = 'muted', className }: BillingKpiTileProps) {
    return (
        <TileContainer className={className} elevation={0}>
            <TileValue title={value}>{value}</TileValue>
            <TileLabel>{label}</TileLabel>
            {hint ? (
                <TileHint sx={{ color: hintTone === 'warning' ? 'var(--warning)' : 'var(--muted)' }}>{hint}</TileHint>
            ) : null}
        </TileContainer>
    )
}
